import {extend, override} from 'flarum/common/extend';
import app from 'flarum/forum/app';
import DiscussionComposer from 'flarum/forum/components/DiscussionComposer';
import ComposerState from 'flarum/forum/states/ComposerState';
import Discussion from 'flarum/common/models/Discussion';
import FieldList from './fields/FieldList';
import Uploader from './utils/Uploader';
import Form from './models/Form';
import isHorizontalLayout from './utils/isHorizontalLayout';
import parseValidationError from './utils/parseValidationError';

function applies(form: Form, composerState: ComposerState): boolean {
    if (!form.link_id()) {
        return true;
    }

    const selectedTags = composerState.fields.tags;

    // If there's no valid tag selection, don't show any scoped form
    if (!Array.isArray(selectedTags)) {
        return false;
    }

    // Show forms for selected tags
    return selectedTags.some(tag => {
        return tag.id() == form.link_id();
    });
}

export default function () {
    extend(DiscussionComposer.prototype, 'oninit', function () {
        if (app.forum.attribute('formulaireNoByobuFields') && this._isByobuComposer) {
            return;
        }

        this.formulaireValidationErrors = {};
        this.formulaireRequestErrorAlert = null;

        // If clarkwinkelmann-composer-page is used, there could be new instances of ComposerBody initialized for a single composer session
        if (this.composer.fields.formulaire) {
            return;
        }
        this.composer.fields.formulaire = {};

        const forms = app.forum.formulaireComposerForms() || [];

        forms.forEach(form => {
            this.composer.fields.formulaire[form.id()!] = {
                value: {},
                uploader: new Uploader(form),
            };
        });
    });

    extend(DiscussionComposer, 'initAttrs', function (returnValue, attrs) {
        // We don't have any way to check if this is a Byobu composer, but it should not matter as the class name won't have any side effect

        if (isHorizontalLayout('DiscussionComposer')) {
            attrs.className += ' Formulaire-horizontal-layout';
        }
    });

    extend(DiscussionComposer.prototype, 'headerItems', function (items) {
        if (app.forum.attribute('formulaireNoByobuFields') && this._isByobuComposer) {
            return;
        }

        const forms = app.forum.formulaireComposerForms() || [];

        const template: any[] = [];

        forms.forEach(form => {
            if (!applies(form, this.composer)) {
                return;
            }

            const state = this.composer.fields.formulaire[form.id()!];

            const list = new FieldList({
                configs: form.template()!,
                submission: state.value,
                uploader: state.uploader,
                disabled: this.loading || state.uploader.uploading,
                validationErrors: (this.formulaireValidationErrors.formulaireForms || {})[form.id()!],
            });

            template.push(list.listView());
        });

        if (template.length) {
            items.add('formulaire', m('.FormulaireDiscussionComposer', template));
        }
    });

    extend(DiscussionComposer.prototype, 'data', function (data: any) {
        if (app.forum.attribute('formulaireNoByobuFields') && this._isByobuComposer) {
            return;
        }

        // Probably not the most descriptive hook to reset the validation errors,
        // but we need to do it just before the next request and that's exactly when this hook runs
        this.formulaireValidationErrors = {};

        if (this.formulaireRequestErrorAlert !== null) {
            app.alerts.dismiss(this.formulaireRequestErrorAlert);
        }

        const forms = app.forum.formulaireComposerForms() || [];

        const formData: any[] = [];

        forms.forEach(form => {
            if (!applies(form, this.composer)) {
                return;
            }

            formData.push({
                verbatim: true, // Flarum workaround, handled in patchStore
                id: form.id(),
                attributes: {
                    data: this.composer.fields.formulaire[form.id()!].value,
                },
            });
        });

        if (formData.length) {
            data.relationships = data.relationships || {};
            data.relationships.formulaireForms = formData;
            data.formulaireSaveOptions = {
                errorHandler: (error: any) => {
                    if (error.status === 422 && error.response.errors) {
                        this.formulaireValidationErrors = parseValidationError(error);
                    }

                    m.redraw();

                    // Systematically show the error because otherwise it's not very obvious an error occurred
                    // This also includes non-Formulaire error because we can't easily fallback to the default alert
                    if (error.alert) {
                        this.formulaireRequestErrorAlert = app.alerts.show(error.alert, error.alert.content);
                    }
                },
            };
        }
    });

    override(ComposerState.prototype, 'minimumHeight', function (original) {
        const PrivateDiscussionComposer = (flarum.extensions['fof-byobu'] as any)?.discussions?.PrivateDiscussionComposer;

        if (PrivateDiscussionComposer && app.forum.attribute('formulaireNoByobuFields') && this.bodyMatches(PrivateDiscussionComposer)) {
            return;
        }

        const originalHeight = original();

        if (!this.bodyMatches(DiscussionComposer)) {
            return originalHeight;
        }

        const forms = app.forum.formulaireComposerForms() || [];

        let formulaireHeight = 0;

        forms.forEach(form => {
            if (!applies(form, this)) {
                return;
            }

            const template = form.template();

            if (!template) {
                return;
            }

            template.forEach(field => {
                switch (field.type) {
                    case 'items':
                    case 'upload':
                        // Items and Upload will change height as the user types into them, so we'll make a basic estimate
                        // but not continually change the composer height during composing
                        formulaireHeight += 120;
                        break;
                    case 'checkbox':
                    case 'radio':
                        formulaireHeight += (field.options || []).length * 36;

                        if (field.other) {
                            formulaireHeight += 36;
                        }

                        break;
                    default:
                        formulaireHeight += 36; // Standard input field height
                }

                if (!isHorizontalLayout('DiscussionComposer')) {
                    formulaireHeight += 31; // label is 21px + 10px for margin bottom
                }

                formulaireHeight += 10; // .Form-group padding bottom
            });
        });

        // This hard-coded maximum height is also hard-coded in the CSS to enable scrolling
        return originalHeight + Math.min(formulaireHeight, 500);
    });

    // Flarum doesn't allow setting a custom error handler on ComposerBody.onsubmit
    // We'll work around that by allowing to pass a special name in the save data which will be moved to the request options
    override(Discussion.prototype, 'save', function (original, attributes, options = {}) {
        const {
            formulaireSaveOptions,
            ...newAttributes
        } = attributes;

        const additionalOptions = (formulaireSaveOptions as { [key: string]: any }) || {};

        return original(newAttributes, {
            ...options,
            ...additionalOptions
        });
    });
}
